import React from 'react';
import IconSystem from 'icon-collection/icon-systems';

// ============================================================================
// TYPES
// ============================================================================

/**
 * After the April 2026 reorganization this section carries only the truly
 * "additional" cashier-facing fields. Discount + comp toggle moved into
 * `DiscountSection`; advance fields moved into `AdvanceSection`. The shape
 * below is what BookingFormModal hands in — it still holds isComplimentary
 * etc. for state continuity, but we only render the three small fields here.
 */
export interface AdditionalData {
  isVip: boolean;
  isComplimentary: boolean;          // owned by DiscountSection now (kept for state shape)
  complimentaryReason: string;       // owned by DiscountSection now
  specialNotes: string;
  expectedArrivalTime: string;
  advanceDueDate: string;            // owned by AdvanceSection now
  advanceRequired: number;           // owned by AdvanceSection now
  advanceOverrideRemarks: string;    // owned by AdvanceSection now
}

interface AdditionalDetailsSectionProps {
  data: AdditionalData;
  onChange: (updates: Partial<AdditionalData>) => void;
  fieldErrors: Record<string, string>;
  expanded: boolean;
  onToggle: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

const AdditionalDetailsSection: React.FC<AdditionalDetailsSectionProps> = ({
  data, onChange, fieldErrors, expanded, onToggle,
}) => {
  const hasData = data.isVip || !!data.specialNotes || !!data.expectedArrivalTime;
  void fieldErrors; // none of these fields validate today; reserved for future

  return (
    <div>
      <div
        className={`collapsible-section-header${expanded ? ' expanded' : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <IconSystem.chevronRight size={14} className="chevron" />
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Additional Details
        </span>
        {hasData && (
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--primary)', marginLeft: '0.25rem' }} />
        )}
      </div>

      <div className={`collapsible-section-body${expanded ? ' expanded' : ''}`}>
        <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={data.isVip}
                onChange={e => onChange({ isVip: e.target.checked })}
              />
              VIP Guest
            </label>
          </div>
          <div>
            <label className="form-label">Expected Arrival</label>
            <input
              type="time"
              className="form-input"
              value={data.expectedArrivalTime}
              onChange={e => onChange({ expectedArrivalTime: e.target.value })}
            />
          </div>
        </div>

        <div style={{ marginTop: '0.75rem' }}>
          <label className="form-label">Special Notes</label>
          <textarea
            className="form-textarea"
            value={data.specialNotes}
            onChange={e => onChange({ specialNotes: e.target.value })}
            rows={3}
            maxLength={1000}
            placeholder="Any special requests or notes..."
            style={{ resize: 'vertical', minHeight: '60px' }}
          />
        </div>
      </div>
    </div>
  );
};

export default AdditionalDetailsSection;
